import type {
  Condition,
  Configuration,
  SearchLogsInput,
} from '@repo/api-gw-sdk';
import { LlgPoliciesApi, SearchApi } from '@repo/api-gw-sdk';
import type { PaginationState } from '@tanstack/react-table';

import type {
  EntryLogsQueryResponse,
  LogsSpaceSchemasResponse,
  LogsSpaceSummaryResponse,
} from '@/components/logs-space/types';
import { toJsonText } from '@/components/queryBuilder/QueryLanguageConverter';

import type { HttpClient } from './httpClient';

const baseUrl = '/logs';

export const logs = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const llgPoliciesApi = new LlgPoliciesApi(config);
  const searchApi = new SearchApi(config);

  return {
    useList: (
      pagination: PaginationState,
      condition: Condition | undefined,
      filter: string,
      dateRange: { startDate: string; endDate: string }
    ) =>
      httpClient.execute(
        (
          ...args: [
            string,
            {
              condition: Condition | undefined;
              pagination: PaginationState;
              filter?: string;
              dateRange: { startDate: string; endDate: string };
            },
          ]
        ) =>
          httpClient.fetch(
            `/projects/${args[0]}/logs/query?pageIndex=${args[1].pagination.pageIndex}&pageSize=${args[1].pagination.pageSize}${args[1].condition ? `&filter=${encodeURIComponent(toJsonText(args[1].condition))}` : undefined}&query=${args[1].filter ? encodeURIComponent(args[1].filter) : ''}&startDate=${encodeURIComponent(args[1].dateRange.startDate)}&endDate=${encodeURIComponent(args[1].dateRange.endDate)}`
          ) as Promise<EntryLogsQueryResponse>,
        baseUrl,
        '/query',
        currentProjectId,
        { condition, pagination, filter, dateRange }
      ),
    useListSchemas: () =>
      httpClient.execute(
        (...args: [string]) =>
          httpClient.fetch(
            `/projects/${args[0]}/logs/schemas`
          ) as Promise<LogsSpaceSchemasResponse>,
        baseUrl,
        '/schemas',
        currentProjectId
      ),
    useLogsSummary: (
      query: Condition | undefined,
      filter: string,
      dateRange: { startDate: string; endDate: string }
    ) =>
      httpClient.execute(
        (
          ...args: [
            string,
            {
              query: Condition | undefined;
              filter: string;
              dateRange: { startDate: string; endDate: string };
            },
          ]
        ) =>
          httpClient.fetch(
            `/projects/${args[0]}/logs/summary?query=${args[1].filter ? encodeURIComponent(args[1].filter) : ''}${
              args[1].query
                ? `&filter=${encodeURIComponent(toJsonText(args[1].query))}`
                : ''
            }&startDate=${encodeURIComponent(args[1].dateRange.startDate)}&endDate=${encodeURIComponent(args[1].dateRange.endDate)}`
          ) as Promise<LogsSpaceSummaryResponse>,
        baseUrl,
        '/summary',
        currentProjectId,
        { query, filter, dateRange }
      ),
    useSearch: (searchLogsInput: SearchLogsInput) =>
      httpClient.execute(
        searchApi.searchLogs.bind(searchApi),
        baseUrl,
        '/search',
        currentProjectId,
        searchLogsInput
      ),
    policies: {
      useList: () =>
        httpClient.execute(
          llgPoliciesApi.listLlgPolicies.bind(llgPoliciesApi),
          baseUrl,
          '/policies',
          currentProjectId
        ),
    },
  };
};
